import { get } from '../../helpers/fetch';
import { buildURL } from '../../helpers/urls';
import { can } from '../../components/Authorize';
import { CONFIG } from '../../components/Authorize/permissions/resources';
import { normalizeCam } from '../Mapping/Arrange/logic';
import { fetchCamMapJson, getRowDictFile } from '../Mapping/mapLogic';
import {
  mapPool, scanIdsFromOption, sortTiles, tileBoxesKey,
} from './helpers';
import { parseRekognition } from './parse';
import { cachedFetch } from './signedAssetCache';
import {
  EMPTY, buildOverlay, mergeOverlays,
} from './srs/geometry';

export { parseRekognition };

export const getCrowdCountSessions = (slugDate) => {
  const params = new URLSearchParams({ slugDate });
  return get(buildURL('crowd-count', 'sessions', `?${params}`));
};

/** Scan with tile media S3-presigned (~10min). FE caches bytes by path for ~7d. */
export const getCrowdCountScan = (id) => get(buildURL('crowd-count', id));

/** Fetch tile detection JSON via signed-asset cache. */
export const fetchJson = async (url) => {
  if (!url) return null;
  try {
    const res = await cachedFetch(url);
    return res?.ok ? res.json() : null;
  } catch {
    return null;
  }
};

const tilesForOption = (option, scans) => {
  const ids = scanIdsFromOption(option);
  const isGroup = option?.kind === 'session' && ids.length >= 2;
  if (isGroup) {
    return scans.flatMap((scan) => {
      const scanId = String(scan._id);
      return (scan.tiles || []).map((t) => ({ ...t, _scanId: scanId }));
    });
  }
  return scans[0]?.tiles || [];
};

/** Fetch Rekognition JSON for tiles → { tileBoxes, rekognitionLabels }. */
export const loadDetections = async (tiles) => {
  if (!tiles?.length) return { tileBoxes: {}, rekognitionLabels: [] };

  const entries = await mapPool(tiles, 6, async (tile) => {
    const key = tileBoxesKey(tile);
    const [faces, labels] = await Promise.all([
      fetchJson(tile.rekognitionFacesUrl),
      fetchJson(tile.rekognitionLabelsUrl),
    ]);
    return [key, parseRekognition(key, faces, labels)];
  });

  const tileBoxes = {};
  const rekognitionLabels = [];
  entries.forEach(([key, parsed]) => {
    tileBoxes[key] = parsed.boxes;
    rekognitionLabels.push(...(parsed.labels || []));
  });
  return { tileBoxes, rekognitionLabels };
};

/** Load seat overlay for current scans. Returns EMPTY when disabled or no scans. */
export async function loadSrsOverlay(scans) {
  if (!can(CONFIG) || !scans?.length) return EMPTY;

  const cams = [...new Set(scans.map((s) => normalizeCam(s?.camera)).filter(Boolean))];
  const [rowDict, ...camEntries] = await Promise.all([
    getRowDictFile().catch(() => null),
    ...cams.map(async (cam) => {
      try {
        return [cam, await fetchCamMapJson(cam)];
      } catch {
        return [cam, null];
      }
    }),
  ]);

  const byCam = new Map(camEntries);
  const multi = scans.length > 1;
  return mergeOverlays(scans.map((scan) => {
    const tiles = sortTiles(scan.tiles).map((t) => (
      multi ? { ...t, _scanId: String(scan._id) } : t
    ));
    return buildOverlay(byCam.get(normalizeCam(scan.camera)), tiles, scan, rowDict);
  }));
}

/** Phase 1 — scan docs only (fast path for mosaic paint). */
export const loadScanDocs = async (option) => {
  const ids = scanIdsFromOption(option);
  if (!option?.value) {
    return { scans: [], error: false };
  }
  if (!ids.length) {
    return { scans: [], error: true };
  }

  const docs = await Promise.all(ids.map((id) => getCrowdCountScan(id).catch(() => null)));
  const scans = docs.filter(Boolean);
  return {
    scans,
    error: !scans.length,
  };
};

/** Phase 2 — detections for already-loaded scans. */
export const loadDetectionsForOption = async (option, scans) => {
  const tiles = tilesForOption(option, scans);
  return loadDetections(tiles);
};
